<template>
  <div v-if="visible" class="cem-overlay" @mousedown.self="close" role="presentation">
    <section class="cem-panel" role="dialog" aria-modal="true" aria-labelledby="cem-title">
      <header class="cem-header">
        <div class="cem-heading"><span class="cem-icon"><i class="fas fa-envelope"></i></span><div>
          <h2 id="cem-title">Enviar enlace de contrato</h2>
          <p>Contrato de {{ Number(contractType) === 1 ? 'servicio' : 'obra' }} · {{ Number(sourceType) === 1 ? 'Presupuesto' : 'Gasto recurrente' }}</p>
        </div></div>
        <button type="button" aria-label="Cerrar" class="cem-close" :disabled="sending" @click="close"><i class="fas fa-times"></i></button>
      </header>
      <div class="cem-body">
        <div class="cem-info"><i class="fas fa-shield-alt"></i><span>Se enviará el mismo enlace vigente a los destinatarios autorizados. El enlace no se renovará.</span></div>
        <label for="cem-provider">Correo del proveedor</label>
        <input id="cem-provider" v-model.trim="provider" type="email" autocomplete="email" placeholder="proveedor@ejemplo.com" :disabled="sending" />
        <label for="cem-extras">Otros correos <span class="cem-muted">(separados por comas)</span></label>
        <textarea id="cem-extras" v-model="extras" rows="2" :disabled="sending" placeholder="administracion@ejemplo.com, juridico@ejemplo.com"></textarea>
        <div class="cem-hint">Hasta 10 destinatarios en total. Se enviará un correo individual a cada uno.</div>
        <label for="cem-message">Mensaje personalizado</label>
        <textarea id="cem-message" v-model="message" rows="5" maxlength="2000" :disabled="sending"></textarea>
        <div class="cem-count">{{ message.length }} / 2000</div>
        <div class="cem-summary"><i class="far fa-clock"></i><div><strong>Vigencia del enlace</strong><span>{{ expiresAt || 'Consultar en el listado' }}</span></div></div>
        <div v-if="validationError" class="cem-error" role="alert">{{ validationError }}</div>
      </div>
      <footer class="cem-footer">
        <span class="cem-count-email">{{ recipients.length }} {{ recipients.length === 1 ? 'destinatario' : 'destinatarios' }}</span>
        <div class="cem-actions"><button type="button" class="cem-btn cem-neutral" :disabled="sending" @click="close">Cancelar</button><button type="button" class="cem-btn cem-primary" :disabled="sending || !requestId" @click="send"><i :class="sending ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane'"></i> {{ sending ? 'Enviando…' : 'Enviar correo' }}</button></div>
      </footer>
    </section>
  </div>
</template>

<script>
import request from '@/services/request';

export default {
  name: 'ContractEmailModal',
  props: {
    visible: { type: Boolean, default: false },
    requestId: { type: [String, Number], default: null },
    contractType: { type: [String, Number], default: 1 },
    sourceType: { type: [String, Number], default: 1 },
    providerEmail: { type: String, default: '' },
    expiresAt: { type: String, default: '' }
  },
  data() {
    return {
      provider: '', extras: '', message: '', sending: false, validationError: ''
    };
  },
  computed: {
    recipients() {
      return Array.from(new Set(
        [this.provider].concat(this.extras.split(','))
          .map(email => email.trim().toLowerCase()).filter(Boolean)
      ));
    }
  },
  watch: {
    visible(open) {
      if (open) this.reset();
    }
  },
  methods: {
    reset() {
      this.provider = this.providerEmail || '';
      this.extras = '';
      this.message = 'Buen día.\n\nCompartimos el enlace para revisar y firmar el contrato correspondiente.\n\nSaludos cordiales,\nGrupo M.';
      this.sending = false;
      this.validationError = '';
    },
    close() {
      if (!this.sending) this.$emit('close');
    },
    async send() {
      if (this.sending) return;
      this.validationError = '';
      const emails = this.recipients;
      const emailRegex = /^[^\s@,]+@[^\s@,]+\.[^\s@,]+$/;
      if (!emails.length) {
        this.validationError = 'Agrega al menos un correo electrónico.';
        return;
      }
      if (emails.length > 10) {
        this.validationError = 'Se permiten máximo 10 destinatarios.';
        return;
      }
      if (emails.some(email => !emailRegex.test(email))) {
        this.validationError = 'Hay un correo no válido. Separa los destinatarios con comas.';
        return;
      }
      if (this.message.length > 2000) {
        this.validationError = 'El mensaje supera los 2,000 caracteres.';
        return;
      }
      if (!this.requestId) {
        this.validationError = 'No existe una solicitud de firma vigente.';
        return;
      }
      this.sending = true;
      try {
        const response = await request.post(
          `/budget-buy-contracts/requests/${encodeURIComponent(this.requestId)}/send-link`,
          { emails, message: this.message.trim() }
        );
        const result = response.data || {};
        const info = result.data || {};
        this.$emit('sent', { response: result, accepted: info.accepted || [], failed: info.failed || [] });
      } catch (error) {
        const response = error && error.response && error.response.data;
        this.validationError = (response && response.message) || 'No fue posible confirmar el envío. Consulta el estado antes de reintentar.';
      } finally {
        this.sending = false;
      }
    }
  }
};
</script>

<style scoped>
.cem-overlay{position:fixed;inset:0;z-index:2000;background:rgba(17,31,49,.62);display:flex;align-items:center;justify-content:center;padding:20px;overflow-y:auto}
.cem-panel{width:100%;max-width:620px;border-radius:16px;overflow:hidden;background:#fff;box-shadow:0 24px 90px rgba(4,22,46,.25);color:#1b2c40;font:14px/1.5 Inter,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif}
.cem-header{padding:23px 25px;display:flex;align-items:flex-start;justify-content:space-between;border-bottom:1px solid #e9edf2}
.cem-heading{display:flex;gap:13px;align-items:center}.cem-icon{display:grid;place-items:center;width:42px;height:42px;border-radius:11px;background:#eaf0fb;color:#2456ac;font-size:17px}.cem-heading h2{font-size:19px;font-weight:700;margin:0}.cem-heading p{font-size:12px;color:#758298;margin:3px 0 0}
.cem-close{border:0;background:transparent;color:#64748b;padding:5px;cursor:pointer}
.cem-body{padding:24px 25px;max-height:65vh;overflow-y:auto}.cem-info{display:flex;align-items:flex-start;gap:10px;border:1px solid #dbe7f7;border-radius:10px;background:#f4f8fd;padding:11px 12px;margin-bottom:20px;color:#416083;font-size:12px}.cem-info i{margin-top:2px}
.cem-body label{display:block;font-size:12px;font-weight:700;margin:17px 0 7px}.cem-body input,.cem-body textarea{display:block;width:100%;border-radius:9px;border:1px solid #cad5e4;background:#fff;padding:11px 13px;color:#203047;outline:none;resize:vertical;font-size:13px;line-height:1.5;font-family:inherit}.cem-body input:focus,.cem-body textarea:focus{border-color:#2862c5;box-shadow:0 0 0 3px rgba(40,98,197,.1)}.cem-body input:disabled,.cem-body textarea:disabled{opacity:.6}.cem-muted,.cem-hint,.cem-count{color:#7c8999;font-size:11px;font-weight:400}.cem-hint{margin-top:6px}.cem-count{text-align:right;margin-top:5px}.cem-summary{border-radius:10px;background:#faf7ef;border:1px solid #efe5cb;display:flex;gap:11px;padding:13px;color:#926e2d;margin-top:18px}.cem-summary strong,.cem-summary span{display:block}.cem-summary strong{font-size:12px}.cem-summary span{font-size:12px;color:#675f51}.cem-error{background:#fff1ef;border:1px solid #ecc8c3;color:#af403b;border-radius:8px;padding:10px 12px;margin-top:15px;font-size:12px}
.cem-footer{display:flex;gap:10px;justify-content:space-between;align-items:center;border-top:1px solid #e9edf2;padding:17px 25px}.cem-count-email{color:#79879a;font-size:11px}.cem-actions{display:flex;gap:10px}.cem-btn{border:1px solid #cbd6e4;border-radius:8px;padding:10px 16px;font-weight:600;font-size:12px;cursor:pointer}.cem-btn:disabled{opacity:.6;cursor:wait}.cem-neutral{background:#fff;color:#3c4a5b}.cem-primary{background:#2459b9;color:#fff;border-color:#2459b9}.cem-primary:hover:not(:disabled){background:#1c4899}
@media(max-width:550px){.cem-overlay{align-items:flex-start;padding:10px}.cem-header,.cem-body{padding:18px}.cem-footer{padding:14px 18px;align-items:stretch;flex-direction:column}.cem-actions{justify-content:flex-end}.cem-heading h2{font-size:16px}}
</style>
